import { useRef } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { Input, Switch, Textarea } from "@bookdental/ui";
import { reduzirImagem } from "@/lib/reduzir-imagem";
import { cn } from "@/lib/cn";
import type { EditorDeAparencia } from "./use-aparencia-do-catalogo";

const opcao = (ativa: boolean) =>
  cn(
    "flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors",
    ativa ? "border-brand-600 bg-brand-50 text-brand-700 dark:bg-primary/10 dark:text-primary" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted",
  );

/** A capa: ligar, título, apresentação e o fundo (a cor de destaque ou uma imagem própria). */
export function CampoDaCapa({ ed }: { ed: EditorDeAparencia }) {
  const input = useRef<HTMLInputElement>(null);
  const capa = ed.rascunho!.capa;

  const escolher = async (file: File) => {
    if (!file.type.startsWith("image/")) return toast.error("Selecione um arquivo de imagem.");
    try {
      ed.setCapa(await reduzirImagem(file, 1600, 0.82));
      ed.mudar("capa", { estilo: "imagem" });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="space-y-2.5">
      <label className="flex items-center justify-between gap-2 text-[13px] text-zinc-700 dark:text-foreground">
        Primeira página de capa
        <Switch checked={capa.ativa} onCheckedChange={(v) => ed.mudar("capa", { ativa: v })} />
      </label>
      {capa.ativa && (
        <>
          <Input value={capa.titulo} maxLength={60} placeholder="Catálogo de serviços" onChange={(e) => ed.mudar("capa", { titulo: e.target.value })} aria-label="Título da capa" />
          <Textarea value={capa.texto} maxLength={400} rows={3} placeholder="Uma apresentação curta do laboratório." onChange={(e) => ed.mudar("capa", { texto: e.target.value })} aria-label="Texto de apresentação" />
          <div className="flex gap-1.5">
            <button type="button" className={opcao(capa.estilo === "cor")} onClick={() => ed.mudar("capa", { estilo: "cor" })}>Fundo na cor</button>
            <button type="button" className={opcao(capa.estilo === "imagem")} onClick={() => (ed.capaUrl ? ed.mudar("capa", { estilo: "imagem" }) : input.current?.click())}>
              Imagem própria
            </button>
          </div>
          {capa.estilo === "imagem" && (
            <div className="flex items-center gap-2">
              {ed.capaUrl ? <img src={ed.capaUrl} alt="Imagem da capa" className="h-14 w-20 rounded-md object-cover" /> : <span className="text-xs text-zinc-500">Nenhuma imagem ainda.</span>}
              <button type="button" onClick={() => input.current?.click()} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline dark:text-primary">
                <ImagePlus className="h-3.5 w-3.5" />
                {ed.capaUrl ? "Trocar" : "Enviar imagem"}
              </button>
              {ed.capaUrl && (
                <button type="button" onClick={() => { ed.setCapa(null); ed.mudar("capa", { estilo: "cor" }); }} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline dark:text-red-400">
                  <Trash2 className="h-3.5 w-3.5" />
                  Remover
                </button>
              )}
            </div>
          )}
          <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void escolher(f); }} />
        </>
      )}
    </div>
  );
}
